import BeePlugin from '@mailupinc/bee-plugin';
import { IPluginFilePicker } from '@mailupinc/bee-plugin/dist/types/bee';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectEmail, selectFeatureFlags } from 'core/userContext/selectors';
import { PanelIntent, PanelView } from 'modules/creativeLibrary/types';
import { isMovableInk } from 'modules/movableInk/utils';
import { useContentEditorPreferencesContext } from 'modules/templateEditor/context/ContentEditorPreferencesContext';
import { useEventDrivenPromise } from 'utils/hooks/useEventDrivenPromise';
import { usePrevious } from 'utils/hooks/usePrevious';
import noop, { NoOp } from 'utils/noop';
import { isString } from 'utils/tsHelpers';

import { Container } from './DragAndDropEditor.styled';
import { DragAndDropEditorModals } from './DragAndDropEditorModals';
import {
  ALL_SAVED_ROWS_CATEGORY,
  BEE_PLUGIN_CONTAINER_ID,
  DYNAMIC_CONTENT_BUILDER_ADDON_HANDLE,
  MOVABLE_INK_ADDON_HANDLE,
} from './constants';
import {
  DragAndDropEditorModalType,
  DragAndDropEditorModalsProvider,
  DragAndDropEditorProvider,
  useDragAndDropEditorContext,
  useDragAndDropEditorModalsContext,
} from './context';
import { useDynamicContentBuilderAddOn } from './hooks/useDynamicContentBuilderAddOn';
import { useMovableInkAddOn } from './hooks/useMovableInkAddOn';
import { useLatestValueRef } from './hooks/useLatestValueRef';
import {
  AnchorTarget,
  BeeConfig,
  ContentModuleType,
  DeleteSavedRowResponse,
  DragAndDropEditorError,
  DragAndDropEditorProps,
  GetContentCallback,
  Hooks,
  ImageContentModule,
  OnCreateSavedRow,
  OnDeleteSavedRow,
  OnEditSavedRow,
  OnManageCustomCategories,
} from './types';
import {
  createAddOnContentDialogHandler,
  createCurrentElt,
  createCurrentImgElt,
  createMixedContentModule,
  filterDeletedRow,
  getEditorConfig,
  getEditorFonts,
  getRowsConfiguration,
  getSavedRowsConfig,
  getSavedRowsFilterVariables,
  isAuthTokenDefined,
  isAuthTokenUnexpired,
  toBeeSavedRows,
} from './utils';

export const DnDEditor = ({
  contentLocale = '',
  initTs = 0,
  initValue = '{}',
  onChange,
  onGetContentRef,
  onUpdateValues,
  onEditorLoad,
  editSavedRowDesign,
}: DragAndDropEditorProps) => {
  const { customFonts, getCustomFonts, isFetchingCustomFonts } =
    useContentEditorPreferencesContext();
  const {
    authToken,
    getBeeAuthToken,
    getMergeParams,
    isFetchingAuthToken,
    isFetchingMergeParams,
    mergeParams,
    projectUID,
    trackError,
    messageCategories,
    getMessageCategories,
    isFetchingCategories,
    getSavedRows,
    deleteSavedRow,
    parseSavedRowProcess,
    isSavingTemplateForSavedRow,
  } = useDragAndDropEditorContext();
  const { openModal, closeModal, openedModals } = useDragAndDropEditorModalsContext();

  const dynamicContentBuilderAddOn = useDynamicContentBuilderAddOn();
  const movableInkAddOn = useMovableInkAddOn();
  const userEmail = useSelector(selectEmail);

  const {
    CreativeLibraryInBee,
    MovableInk,
    SavedRows: isSavedRowsFeatureEnabled,
  } = useSelector(selectFeatureFlags);

  const contentLocalePrev = usePrevious(contentLocale);
  const initTsPrev = usePrevious(initTs);
  const initValuePrev = usePrevious(initValue);

  const editorConfigRef = useRef<BeeConfig>({} as BeeConfig);
  const editorInstanceRef = useRef<BeePlugin>();
  const getContentCallbackRef = useRef<GetContentCallback>(noop);

  /**
   * This is used to track when the saved rows modal is open for usage in the bee config.
   * This is especially needed because react does not update its states fast enough to access the latest state value in the bee config.
   * (e.g. the bee config may have an outdated reference/value to react states after they're updated)
   */
  const isSavedRowsOpenRef = useRef<boolean>(false);

  /**
   * This value is used to track the id of the saved row that was last deleted.
   * This is used to filter out the deleted row from the saved rows list
   * displayed in the Rows tab. This is done because the replica DB we fetch
   * saved rows from does not immediately reflect the deletion of a row at times.
   */
  const deletedSavedRowIdRef = useRef<number | null>(null);

  const onBeeChangeCallback = useCallback(
    (json: string) => {
      /**
       * This function is triggered whenever the bee builder tracks a change in the template json.
       * We do not want to update the `beeBuilderMetadata` when simply creating a new row.
       * We are skipping the update when the saved rows modal is open.
       */
      if (!isSavedRowsOpenRef.current) {
        onChange(json);
      }
    },
    [onChange],
  );
  const onBeeChangeRef = useRef<typeof onBeeChangeCallback>(onBeeChangeCallback);
  const getSavedRowsRef = useRef<typeof getSavedRows>(getSavedRows);

  const onBeeSaveCallbackRef = useRef<(json: string, html: string) => void>(
    (json: string, html: string) => onUpdateValues(json, html, getContentCallbackRef.current),
  );
  const onEditorLoadCallbackRef = useRef<() => void>(onEditorLoad);
  const [isEditorStarted, setIsEditorStarted] = useState<boolean>(false);
  const [initAPIsHaveResolved, setInitAPIsHaveResolved] = useState<boolean>(false);
  const canLoadEditor =
    !isFetchingAuthToken &&
    !isFetchingCustomFonts &&
    !isFetchingMergeParams &&
    !isFetchingCategories;
  const canLoadEditorPrev = usePrevious(canLoadEditor) ?? true;
  const shouldInitEditor = initAPIsHaveResolved && canLoadEditor && !isEditorStarted;
  // the Creative Library implementation is simply for POC and is not meant for internal or public
  // Beta until TNT-7098 is complete
  const useCreativeLibrary = CreativeLibraryInBee;

  const { toggleSavedRowDesignEditorRef, isEditingSavedRowDesign } = editSavedRowDesign;

  /**
   * To get the generated html output from the bee editor, we must trigger the instance's save
   * method (which can be configured with a callback), however, that callback does not accept any
   * additional outside params (https://docs.beefree.io/configuration-parameters/)
   *
   * There are inevitably multiple things we would like to do with this callback which may differ
   * depending on the trigger that was used to initiate it, such as
   * - save the template (with the returned json and html values)
   * - save the template and then continue to the next part of a workflow
   * - simply update the state values for the template (html & json metadata)
   *
   * So by passing up getContent to the parent's onGetContentRef prop, we can allow the parent to call
   * onGetContentRef with a custom callback, which is held in a local ref and then sent back to the
   * parent when calling onUpdateValues
   */
  const getContent = (callback = noop) => {
    getContentCallbackRef.current = callback;
    // warning: if the editor is not ready, save() will not fire (example, when switching to a newly
    // created locale)
    editorInstanceRef.current?.save();
  };

  // The 'filePicker' for Creative Library (when it's FF is enabled), or for new Movable Ink blocks
  // https://docs.beefree.io/beefree-sdk/builder-addons/addons/custom-addons/addon-development#image
  const handleOpenImage = (
    resolve: (data: any) => void, // 2nd parameter when data is IPluginFilePicker - options?: Record<string,unknown> does what?
    reject: NoOp,
    args: Record<string, any>,
  ) => {
    // when inserting a new image...
    // - cl image:  args: { url: { alt: "", src: "", href: "", target: "_blank" }, context: "imageModule.src" }
    // - mi image:  args: { value: { alt: "", src: "", href: "", target: "_blank" }, contentDialogId: "movableInk" }
    // when changing an image...
    // - cl image:  args: { url: "url('[imageUrl]')", context: "imageModule.src", ref: "imageModule.src" }
    // - mi image:  args: { value: { alt, src, href, target, type: "image", height: "[natural height]px", width: "[natural width]px" }, contentDialogId: "movableInk" }
    const selectedImageElement =
      (args.context === 'imageModule.src' && isString(args.url) && createCurrentImgElt(args.url)) ||
      (args.contentDialogId === 'movableInk' &&
        !!args.value?.src &&
        createCurrentElt('img', { alt: args.value.alt || '', src: args.value.src })) ||
      undefined;
    const isMovableInkImg =
      isMovableInk(selectedImageElement?.getAttribute('src') || '') ||
      args.contentDialogId === MOVABLE_INK_ADDON_HANDLE;
    const selectedAnchorElement =
      isMovableInkImg && !!args.value?.href
        ? createCurrentElt('a', { href: args.value.href })
        : undefined;
    if (MovableInk && isMovableInkImg) {
      openModal(DragAndDropEditorModalType.MovableInkPanel, {
        canStyle: false,
        onInsert: (imgAttrs: Record<string, string>, linkAttrs: Record<string, string>) => {
          resolve({
            type: ContentModuleType.image,
            value: {
              alt: imgAttrs.alt,
              href: linkAttrs.href,
              src: imgAttrs.src,
              target: AnchorTarget.blank,
            },
          } as ImageContentModule);
        },
        onCancel: () => {
          reject();
          closeModal(DragAndDropEditorModalType.MovableInkPanel);
        },
        onClose: () => closeModal(DragAndDropEditorModalType.MovableInkPanel),
        selectedEditorImageNode: selectedImageElement,
        selectedEditorImageAnchorNode: selectedAnchorElement,
      });
    } else {
      openModal(DragAndDropEditorModalType.CreativeLibraryPanel, {
        onInsert: (imgAttrs: Record<string, string>) => {
          resolve({ url: imgAttrs.src } as IPluginFilePicker);
        },
        onCancel: () => {
          reject();
          closeModal(DragAndDropEditorModalType.CreativeLibraryPanel);
        },
        initView: selectedImageElement ? PanelView.Detail : PanelView.List,
        intent: selectedImageElement ? PanelIntent.Edit : PanelIntent.Insert,
        onClose: () => closeModal(DragAndDropEditorModalType.CreativeLibraryPanel),
        selectedEditorImageNode: selectedImageElement,
      });
    }
  };

  const handleOpenDynamicContentBuilder = (
    resolve: (obj: Record<string, any>) => void,
    reject: NoOp,
  ) => {
    const onInsert = (content: string) => {
      closeModal(DragAndDropEditorModalType.DynamicContentBuilder);
      resolve(createMixedContentModule(content));
    };

    openModal(DragAndDropEditorModalType.DynamicContentBuilder, {
      onInsert,
      onCancel: reject,
      onClose: () => {
        reject();
        closeModal(DragAndDropEditorModalType.DynamicContentBuilder);
      },
    });
  };

  /**
   * Problem:
   * When a user is creating a new save row, we open the save row slideover and resolve the `saveRow` `handler` immediately. This allows us to gain access to necessary bee row json and html data.
   * However, calling the `resolve` function causes bee to think that we are done creating the row and need to refetch the new rows; This is the only way refetching of rows is triggered after a new row is created.
   * Bee does not provide a way to manually fetch/refetch saved rows, but instead relies on the `getRows` hook to fetch the rows when bee deem fit to call it.
   *
   * Solution:
   * We create a promise that blocks fetching of saved rows until the slideover is closed. This promise is resolved when the slideover is closed.
   */
  const {
    startEvent: startCreatingNewSavedRow,
    stopEvent: stopCreatingNewSavedRow,
    promiseRef: isCreatingNewSavedRowRef,
  } = useEventDrivenPromise();

  const onSaveRowCallback = useCallback(
    (beeBuilderMetadata: string, rowHTML: string) => {
      parseSavedRowProcess?.stop({
        htmlContent: rowHTML,
        beeMetadataContent: beeBuilderMetadata,
      });
    },
    [parseSavedRowProcess?.stop],
  );

  const onCreateSavedRow: OnCreateSavedRow = useCallback(
    async (resolve) => {
      /**
       * this creates a promise which block fetching of saved rows until the slideover is closed
       * @see `useEventDrivenPromise` for more details
       */
      startCreatingNewSavedRow();

      isSavedRowsOpenRef.current = true;
      parseSavedRowProcess?.start();

      openModal(DragAndDropEditorModalType.CreateOrEditSavedRow, {
        onCloseCallback: () => {
          isSavedRowsOpenRef.current = false;
          stopCreatingNewSavedRow();
          parseSavedRowProcess?.reset();
        },
      });

      /**
       * we are resolving this immediately to trigger onSaveRow and get access to the html and bee row json data.
       * This data is needed on the slide over to save the row and display the preview.
       */
      resolve({ name: 'row' });
    },
    [
      openModal,
      startCreatingNewSavedRow,
      stopCreatingNewSavedRow,
      parseSavedRowProcess?.start,
      parseSavedRowProcess?.reset,
    ],
  );

  const onEditSavedRow: OnEditSavedRow = useCallback(
    (resolve, reject, args: any) => {
      openModal(DragAndDropEditorModalType.CreateOrEditSavedRow, {
        savedRowId: args?.row?.metadata?.id,
        onResolveEditBeeRow: resolve,
        onRejectEditBeeRow: reject,
      });
    },
    [openModal],
  );

  const onDeleteSavedRow: OnDeleteSavedRow = useCallback(
    (resolve, _, args: any) => {
      const rowId = args?.row?.metadata?.id;
      deleteSavedRow({
        variables: { rowId },
        onCompleted: (data: DeleteSavedRowResponse) => {
          const deleteSuccessful = !data?.deleteBeeRow?.failure;

          if (deleteSuccessful) {
            deletedSavedRowIdRef.current = data.deleteBeeRow.beeRowMetadataId;
          }

          // resolve true if you want to refresh the rows
          // or false if you want to keep the side panel’s current listing
          resolve(deleteSuccessful);
        },
      });
    },
    [deleteSavedRow],
  );

  const onManageCustomCategories: OnManageCustomCategories = useCallback(
    (resolve) => {
      openModal(DragAndDropEditorModalType.ManageCustomCategories, {
        onCloseCallback: () => {
          getMessageCategories(); // refetch message categories so we can have the latest data
          // select the "All saved rows" category by default
          resolve(ALL_SAVED_ROWS_CATEGORY);
        },
      });
    },
    [openModal, getMessageCategories],
  );

  const onCreateSavedRowRef = useLatestValueRef(onCreateSavedRow);
  const onSaveRowCallbackRef = useLatestValueRef(onSaveRowCallback);
  const onEditSavedRowRef = useLatestValueRef(onEditSavedRow);
  const onDeleteSavedRowRef = useLatestValueRef(onDeleteSavedRow);
  const onManageCustomCategoriesRef = useLatestValueRef(onManageCustomCategories);

  useEffect(() => {
    if (!isAuthTokenUnexpired(authToken)) {
      getBeeAuthToken();
    }

    getCustomFonts();
    getMergeParams();

    onGetContentRef.current = getContent;

    return () => {
      // Important: remove the getContent trigger in the context if save is not triggered while
      // viewing the Drag-and-Drop Editor

      onGetContentRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (!canLoadEditorPrev && canLoadEditor && !initAPIsHaveResolved) {
      setInitAPIsHaveResolved(true);
    }
  }, [canLoadEditor]);

  /* update onEditorLoadCallbackRef */
  useEffect(() => {
    onEditorLoadCallbackRef.current = onEditorLoad;
  }, [onEditorLoad]);

  /* update onBeeSaveCallbackRef */
  useEffect(() => {
    onBeeSaveCallbackRef.current = (json: string, html: string) => {
      onUpdateValues(json, html, getContentCallbackRef.current);
    };
  }, [onUpdateValues]);

  /* update onBeeChangeRef */
  useEffect(() => {
    onBeeChangeRef.current = onBeeChangeCallback;
  }, [onBeeChangeCallback]);

  /* update getSavedRowsRef */
  useEffect(() => {
    getSavedRowsRef.current = getSavedRows;
  }, [getSavedRows]);

  /* Initialize BeePlugin */
  useEffect(() => {
    // - only run this once following mount (above) (!isEditorStarted)
    // - wait for all api calls to resolve (canLoadEditor && initAPIsHaveResolved)
    // - only init with a valid token
    const authTokenIsValid = isAuthTokenDefined(authToken) && isAuthTokenUnexpired(authToken);

    if (authTokenIsValid && shouldInitEditor) {
      // catch any errors initializing Bee
      try {
        const savedRowsConfig = getSavedRowsConfig(
          isSavedRowsFeatureEnabled,
          messageCategories,
          onSaveRowCallbackRef,
        );

        const addOns = [dynamicContentBuilderAddOn, movableInkAddOn];
        const contentDialog: BeeConfig['contentDialog'] = {
          ...(useCreativeLibrary
            ? {
                filePicker: {
                  handler: async (resolve, reject, args: Record<string, any>) => {
                    handleOpenImage(resolve, reject, args);
                  },
                },
              }
            : {}),
          addOn: {
            label: 'AddOn Content Dialog',
            handler: createAddOnContentDialogHandler({
              [DYNAMIC_CONTENT_BUILDER_ADDON_HANDLE]: handleOpenDynamicContentBuilder,
              [MOVABLE_INK_ADDON_HANDLE]: handleOpenImage,
            }) as any,
          },
          ...(isSavedRowsFeatureEnabled
            ? {
                externalContentURLs: {
                  label: 'Manage custom categories',
                  handler: async (...args) => {
                    onManageCustomCategoriesRef.current?.(...args);
                  },
                },
                saveRow: {
                  handler: async (resolve, reject, args) => {
                    onCreateSavedRowRef.current?.(resolve, reject, args);
                  },
                },
                onEditRow: {
                  handler: async (...args) => {
                    onEditSavedRowRef.current?.(...args);
                  },
                },
                onDeleteRow: {
                  handler: async (...args) => {
                    onDeleteSavedRowRef.current?.(...args);
                  },
                },
              }
            : {}),
        };

        const hooks: Hooks = {
          getRows: {
            handler: async (resolve, reject, args) => {
              // promise does not resolve until save row slideover is closed
              await isCreatingNewSavedRowRef.current;

              const variables = getSavedRowsFilterVariables(args?.value, userEmail || '');
              const response = await getSavedRowsRef.current(variables);
              const fetchedSavedRows = response?.data?.savedRows;

              if (fetchedSavedRows) {
                const rows = deletedSavedRowIdRef.current
                  ? filterDeletedRow(fetchedSavedRows, deletedSavedRowIdRef.current)
                  : fetchedSavedRows;
                deletedSavedRowIdRef.current = null;

                resolve(toBeeSavedRows(rows));
              } else {
                reject();
              }
            },
          },
        };

        const advancedPermissions: BeeConfig['advancedPermissions'] = useCreativeLibrary
          ? {
              /**
               * @see https://docs.beefree.io/beefree-sdk/advanced-options/advanced-permissions#components
               * Types support is not yet available for `components` property
               * */
              ['components' as string]: {
                filePicker: {
                  canUpload: false,
                  maxUploadFileSize: 20 * 1024 * 1024,
                  canChangeImage: true,
                  canApplyEffects: false,
                  canCreateFolder: false,
                  canDeleteFolder: false,
                  canDeleteFile: false,
                  canImportFile: false,
                  canSearchFreePhotos: false,
                  canChangeVideo: true,
                  canSearchFreeVideos: true,
                },
              },
            }
          : undefined;

        editorConfigRef.current = getEditorConfig(
          customFonts,
          hooks,
          mergeParams,
          onBeeChangeRef,
          onBeeSaveCallbackRef,
          onEditorLoadCallbackRef,
          projectUID,
          advancedPermissions,
          contentDialog,
          addOns,
          savedRowsConfig,
        );

        // https://www.npmjs.com/package/@mailupinc/bee-plugin
        // @ts-ignore (there is a TS error in the BeePlugin - it is expecting a string, but requires the JWT)
        editorInstanceRef.current = new BeePlugin(authToken);
        // wait for the 'start' promise to resolve before indicating that the editor has started
        // - there is no built-in BeePlugin event or property to check, but calling other methods on
        //   the instance will require that it has started (otherwise it will throw an error)
        editorInstanceRef.current
          .start(editorConfigRef.current, JSON.parse(initValue))
          .then(() => setIsEditorStarted(true))
          .catch((error: any) => {
            trackError({
              error,
              errorMessage: 'Drag-and-Drop Editor: Start Failure',
              token: authToken,
              errorName: DragAndDropEditorError.DRAG_AND_DROP_EDITOR_START_ERROR,
            });
          });
      } catch (error: any) {
        trackError({
          error,
          errorMessage: 'Drag-and-Drop Editor: Init Failure',
          token: authToken,
          errorName: DragAndDropEditorError.DRAG_AND_DROP_EDITOR_INITIALIZE_ERROR,
        });
      }
    }
  }, [authToken, shouldInitEditor]);

  /*
   * Changes to the locale, initValue, or initTs should trigger a re-initialization of the editor
   * Bee is NOT a controlled component and may remain in the DOM in some scenarios even though it is
   * not visible (example: template editing) so we need ways to re-initialize the editor in response
   * to events
   * We DO want to reinitialize when:
   * - the locale changes (it is not guaranteed that the initValue will change with a locale change,
   *   but it is certainly intended to be treated as different content)
   * - the initValue changes
   * - the initTs changes (this is a failsafe to ensure a re-init when it is not clear if the locale
   *   or initValue have changed (example: "leave without saving"))
   */
  useEffect(() => {
    if (
      canLoadEditor &&
      isEditorStarted &&
      !isEditingSavedRowDesign &&
      !isSavingTemplateForSavedRow &&
      (initValue !== initValuePrev || contentLocale !== contentLocalePrev || initTs !== initTsPrev)
    ) {
      // this should work, but has issues
      // editorInstanceRef.current.reload(JSON.parse(initValue));
      // atomic option
      editorInstanceRef.current?.start(editorConfigRef.current, JSON.parse(initValue)).then(() => {
        setIsEditorStarted(true);
      });
    }
  }, [
    contentLocale,
    contentLocalePrev,
    initTs,
    initTsPrev,
    initValue,
    initValuePrev,
    canLoadEditor,
    isEditorStarted,
    isEditingSavedRowDesign,
    isSavingTemplateForSavedRow,
  ]);

  /* Update BeePlugin custom fonts */
  useEffect(() => {
    // update the BeePlugin config (only the changes) so that the latest custom fonts are available
    // - it is important that the BeePlugin has successfully started before calling loadConfig,
    //   otherwise an error will occur
    if (canLoadEditor && isEditorStarted) {
      // only reload the part of the config we wish to change (updating the entire config causes an error)
      const editorFonts = getEditorFonts(customFonts);
      editorConfigRef.current = { ...editorConfigRef.current, editorFonts };

      editorInstanceRef.current?.loadConfig({ editorFonts });
    }
  }, [customFonts, canLoadEditor, isEditorStarted]);

  /* Update BeePlugin categories for saved rows */
  useEffect(() => {
    // update the BeePlugin config (only the changes) so the latest row categories are available
    // after updating categories
    // it is important that the BeePlugin has successfully started before calling loadConfig,
    // otherwise an error will occur
    if (isSavedRowsFeatureEnabled && canLoadEditor && isEditorStarted) {
      // only reload the part of the config we wish to change (updating the entire config causes an error):
      const rowsConfiguration = getRowsConfiguration(messageCategories);
      editorConfigRef.current = {
        ...editorConfigRef.current,
        rowsConfiguration,
      };

      editorInstanceRef.current?.loadConfig({ rowsConfiguration });
    }
  }, [isSavedRowsFeatureEnabled, canLoadEditor, isEditorStarted, messageCategories]);

  /**
   * Switches between Edit Single Row Mode and regular template editing for saved rows
   * @param pageBeeBuilderMetadata The saved row page metadata to be used to start the editor in Edit Single Row Mode
   */
  const toggleSavedRowDesignEditor = useCallback(
    (pageBeeBuilderMetadata?: string) => {
      try {
        editorConfigRef.current = {
          ...editorConfigRef.current,
          workspace: {
            ...editorConfigRef?.current?.workspace,
            editSingleRow: !!pageBeeBuilderMetadata,
          },
        };

        // use the saved row template if editing a row or re-start template editing
        const template = pageBeeBuilderMetadata
          ? JSON.parse(pageBeeBuilderMetadata ?? '{}')
          : JSON.parse(initValue);

        editorInstanceRef.current
          ?.start(editorConfigRef.current, template)
          .then(() => {
            setIsEditorStarted(true);
          })
          .catch((error: any) => {
            trackError({
              error,
              errorMessage: 'Drag-and-Drop Editor: Edit Single Row Start Failure',
              token: authToken,
              errorName: DragAndDropEditorError.DRAG_AND_DROP_EDITOR_SINGLE_ROW_INITIALIZE_ERROR,
            });
          });
      } catch (error: any) {
        trackError({
          error,
          errorMessage: 'Drag-and-Drop Editor: Edit Single Row Init Failure',
          token: authToken,
          errorName: DragAndDropEditorError.DRAG_AND_DROP_EDITOR_SINGLE_ROW_START_ERROR,
        });
      }
    },
    [initValue, trackError, setIsEditorStarted, editorConfigRef, editorInstanceRef],
  );

  useEffect(() => {
    toggleSavedRowDesignEditorRef.current = toggleSavedRowDesignEditor;
  }, [toggleSavedRowDesignEditorRef, toggleSavedRowDesignEditor]);

  // Check if Creative Library modal is open to disable drag and drop interactions
  const isCreativeLibraryModalOpen =
    openedModals?.some((modal) => modal.type === DragAndDropEditorModalType.CreativeLibraryPanel) ||
    false;
  return (
    <Container
      id={BEE_PLUGIN_CONTAINER_ID}
      data-test={BEE_PLUGIN_CONTAINER_ID}
      isDnDOff={isCreativeLibraryModalOpen}
    />
  );
};

export const DragAndDropEditor = (props: DragAndDropEditorProps) => {
  return (
    <DragAndDropEditorModalsProvider>
      <DragAndDropEditorProvider
        messageMedium={props.messageMedium}
        editSavedRowDesign={props.editSavedRowDesign}
        hasUnsavedTemplateChanges={props.hasUnsavedTemplateChanges}
      >
        <DnDEditor {...props} />
        <DragAndDropEditorModals />
      </DragAndDropEditorProvider>
    </DragAndDropEditorModalsProvider>
  );
};

export default DragAndDropEditor;
